import { useState, useEffect } from 'react'
import { get, post, deleteAPI } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import './Debts.scss'

interface DebtBatch {
  _id: string
  windowStart: string
  windowIndex: number
  totalAmount: number
  amountPaid: number
  remainingAmount: number
  deadline: string
  isOverdue: boolean
  isCleared: boolean
  bookingCount: number
}

interface DebtSummaryData {
  user: {
    _id: string
    name?: string
    org_name?: string
    firstname?: string
    lastname?: string
    email: string
    outstanding?: number
    debt?: number
    graceUntil?: string
    graceNote?: string
    graceGrantedAt?: string
  }
  summary: {
    totalOutstanding: number
    overdueAmount: number
    pendingAmount: number
    overdueBatchCount: number
    pendingBatchCount: number
    isBlocked: boolean
    nextDeadline: string
  }
  batches: DebtBatch[]
}

interface GrantGraceForm {
  graceUntil: string
  note: string
}

function DebtSummary() {
  const { notify, profile } = useGlobalContext()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const userModel = searchParams.get('userModel') || 'User'

  const [data, setData] = useState<DebtSummaryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [showGrantModal, setShowGrantModal] = useState(false)
  const [grantForm, setGrantForm] = useState<GrantGraceForm>({
    graceUntil: '',
    note: '',
  })
  const [submitting, setSubmitting] = useState(false)

  function fetchDebtSummary() {
    setLoading(true)
    get(`admin/debt/summary/${id}?userModel=${userModel}`)
      .then((result: any) => {
        if (result.status === 200) {
          setData(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch debt summary')
      })
  }

  useEffect(() => {
    if (id) {
      fetchDebtSummary()
    }
  }, [id, userModel])

  const handleGrantGrace = () => {
    if (!grantForm.graceUntil) {
      notify('error', 'Please select a grace period date')
      return
    }

    setSubmitting(true)
    post(
      `admin/debt/grace/${id}`,
      {
        userModel: userModel,
        graceUntil: grantForm.graceUntil,
        note: grantForm.note,
      },
      null,
    )
      .then((result: any) => {
        if (result.status === 200 || result.status === 201) {
          notify('success', 'Grace period granted successfully')
          setShowGrantModal(false)
          setGrantForm({ graceUntil: '', note: '' })
          fetchDebtSummary()
        } else {
          notify('error', result.message)
        }
        setSubmitting(false)
      })
      .catch(() => {
        setSubmitting(false)
        notify('error', 'Failed to grant grace period')
      })
  }

  const handleRevokeGrace = () => {
    if (!confirm('Are you sure you want to revoke this grace period?')) {
      return
    }

    deleteAPI(`admin/debt/grace/${id}?userModel=${userModel}`)
      .then((result: any) => {
        if (result.status === 200) {
          notify('success', 'Grace period revoked successfully')
          fetchDebtSummary()
        } else {
          notify('error', result.message)
        }
      })
      .catch(() => {
        notify('error', 'Failed to revoke grace period')
      })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const getUserName = () => {
    if (!data) return ''
    if (userModel === 'Business') {
      return data.user.org_name || data.user.name || 'Unknown Business'
    }
    return data.user.firstname && data.user.lastname
      ? `${data.user.firstname} ${data.user.lastname}`
      : data.user.name || 'Unknown User'
  }

  const getWindowName = (windowIndex: number) => {
    const windows = [
      '00:00 - 06:00',
      '06:00 - 12:00',
      '12:00 - 18:00',
      '18:00 - 00:00',
    ]
    return windows[windowIndex] || 'Unknown'
  }

  if (loading) {
    return (
      <div className='home'>
        <FormLoader position='center' />
      </div>
    )
  }

  if (!data) {
    return (
      <div className='home'>
        <div className='empty flex justify-center'>
          <div className='empty-cover'>
            <Icon icon='mdi:alert-circle-outline' />
            <h1>Debt Summary Not Found</h1>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='home'>
      {showGrantModal && (
        <div className='modal-overlay' onClick={() => setShowGrantModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>Grant Grace Period</h2>
              <Icon
                icon='mdi:close'
                onClick={() => setShowGrantModal(false)}
                style={{ cursor: 'pointer', fontSize: '24px' }}
              />
            </div>
            <div className='modal-body'>
              <div className='form-group'>
                <label>Grace Until *</label>
                <input
                  type='datetime-local'
                  value={grantForm.graceUntil}
                  onChange={(e) =>
                    setGrantForm({ ...grantForm, graceUntil: e.target.value })
                  }
                />
              </div>
              <div className='form-group'>
                <label>Note</label>
                <textarea
                  value={grantForm.note}
                  onChange={(e) =>
                    setGrantForm({ ...grantForm, note: e.target.value })
                  }
                  placeholder='Optional note about the grace period'
                  rows={3}
                />
              </div>
              <div className='modal-actions'>
                <button
                  className='btn-secondary'
                  onClick={() => setShowGrantModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className='btn-primary'
                  onClick={handleGrantGrace}
                  disabled={submitting}
                >
                  {submitting ? 'Granting...' : 'Grant Grace'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className='home_tag flex justify-between'
        style={{ marginBottom: '20px' }}
      >
        <div className='flex items-center' style={{ gap: '10px' }}>
          <Icon
            icon='mdi:arrow-left'
            style={{ fontSize: '24px', cursor: 'pointer' }}
            onClick={() => navigate(-1)}
          />
          <h2>Debt Summary - {getUserName()}</h2>
        </div>
      </div>

      <div className='debt-summary-header'>
        <div className='user-info'>
          <h2>{getUserName()}</h2>
          <p>
            <strong>Type:</strong> {userModel}
          </p>
          <p>
            <strong>Email:</strong> {data.user.email}
          </p>
          <p>
            <strong>Total Outstanding:</strong>{' '}
            <span style={{ color: '#e60000', fontWeight: 'bold' }}>
              {formatCurrency(data.summary.totalOutstanding)}
            </span>
          </p>

          {data.user.graceUntil ? (
            <>
              <span className='grace-badge active'>
                Grace Active Until: {formatDate(data.user.graceUntil)}
              </span>
              {data.user.graceNote && (
                <p style={{ marginTop: '10px' }}>
                  <strong>Grace Note:</strong> {data.user.graceNote}
                </p>
              )}
            </>
          ) : (
            <span className='grace-badge none'>No Active Grace Period</span>
          )}
        </div>

        <div className='summary-stats'>
          <div className='stat-card'>
            <h4>Total Outstanding</h4>
            <p className='total'>
              {formatCurrency(data.summary.totalOutstanding)}
            </p>
          </div>
          <div className='stat-card'>
            <h4>Overdue Amount</h4>
            <p className='overdue'>
              {formatCurrency(data.summary.overdueAmount)}
            </p>
          </div>
          <div className='stat-card'>
            <h4>Pending Amount</h4>
            <p className='pending'>
              {formatCurrency(data.summary.pendingAmount)}
            </p>
          </div>
          <div className='stat-card'>
            <h4>Overdue Batches</h4>
            <p className='overdue'>{data.summary.overdueBatchCount}</p>
          </div>
          <div className='stat-card'>
            <h4>Pending Batches</h4>
            <p className='pending'>{data.summary.pendingBatchCount}</p>
          </div>
          <div className='stat-card'>
            <h4>Blocked Status</h4>
            <p className={data.summary.isBlocked ? 'overdue' : 'pending'}>
              {data.summary.isBlocked ? 'BLOCKED' : 'Active'}
            </p>
          </div>
        </div>
        {profile.role === 'super admin' && (
          <div className='grace-actions'>
            {data.user.graceUntil ? (
              <button className='revoke-btn' onClick={handleRevokeGrace}>
                Revoke Grace Period
              </button>
            ) : (
              <button
                className='grant-btn'
                onClick={() => setShowGrantModal(true)}
              >
                Grant Grace Period
              </button>
            )}
          </div>
        )}
      </div>

      <div className='home_tables'>
        <div className='left'>
          <div className='left_top'>
            <h2>Debt Batches ({data.batches.length})</h2>
          </div>

          {data.batches.length > 0 ? (
            <>
              <div className='mobile-table'>
                {data.batches.map((batch: DebtBatch, i: number) => (
                  <div className='card' key={batch._id}>
                    <div className='flex justify-between row'>
                      <div className='count flex items-center justify-center'>
                        {i + 1}
                      </div>
                      {batch.isCleared ? (
                        <span className='badge badge-success'>Cleared</span>
                      ) : batch.isOverdue ? (
                        <span className='badge badge-danger'>Overdue</span>
                      ) : (
                        <span className='badge badge-warning'>Pending</span>
                      )}
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Window</h4>
                      <p>{getWindowName(batch.windowIndex)}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Window Start</h4>
                      <p>{formatDate(batch.windowStart)}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Total Amount</h4>
                      <p>{formatCurrency(batch.totalAmount)}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Paid</h4>
                      <p className='success'>
                        {formatCurrency(batch.amountPaid)}
                      </p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Remaining</h4>
                      <p className={batch.isOverdue ? 'failed' : ''}>
                        {formatCurrency(batch.remainingAmount)}
                      </p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Deadline</h4>
                      <p>{formatDate(batch.deadline)}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Bookings</h4>
                      <p>{batch.bookingCount}</p>
                    </div>
                  </div>
                ))}
              </div>

              <table className='desktop-table'>
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Window</th>
                    <th>Window Start</th>
                    <th>Total Amount</th>
                    <th>Paid</th>
                    <th>Remaining</th>
                    <th>Deadline</th>
                    <th>Bookings</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.batches.map((batch: DebtBatch, i: number) => (
                    <tr key={batch._id}>
                      <td>{i + 1}</td>
                      <td>
                        <span className='window-badge DF'>
                          Window {batch.windowIndex}
                        </span>
                        <br />
                        <br />
                        <small>{getWindowName(batch.windowIndex)}</small>
                      </td>
                      <td>{formatDate(batch.windowStart)}</td>
                      <td>{formatCurrency(batch.totalAmount)}</td>
                      <td className='success'>
                        {formatCurrency(batch.amountPaid)}
                      </td>
                      <td className={batch.isOverdue ? 'failed' : ''}>
                        {formatCurrency(batch.remainingAmount)}
                      </td>
                      <td>{formatDate(batch.deadline)}</td>
                      <td>{batch.bookingCount}</td>
                      <td>
                        {batch.isCleared ? (
                          <span className='badge badge-success'>Cleared</span>
                        ) : batch.isOverdue ? (
                          <span className='badge badge-danger'>Overdue</span>
                        ) : (
                          <span className='badge badge-warning'>Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className='empty flex justify-center'>
              <div className='empty-cover'>
                <Icon icon='mdi:cash-multiple' />
                <h1>No Debt Batches</h1>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DebtSummary
